import type { EurekaAmountsControl, IPackageType } from '@telecom/shared/domain/permission';
import { getIsOversea } from '@telecom/shared/utils/environment';

export type UserCenterMenuLocale = 'cn' | 'en' | 'jp' | 'tw';

export interface UserCenterMenuItem {
    key?: string;
    href?: string;
    label?: string;
    icon?: string;
    [key: string]: unknown;
}

export interface BuildUserCenterMenuItemsOptions {
    items: UserCenterMenuItem[];
    locale: UserCenterMenuLocale | string;
    isEnterpriseTenant: boolean;
    isPersonalTenant: boolean;
    packages: IPackageType[];
    packagesLoaded: boolean;
    control: EurekaAmountsControl | null;
    createEurekaHref: (pathname: string) => string;
}

interface CreateManagedMenuItemOptions {
    key: string;
    pathname: string;
    label: string;
    icon: string;
    createEurekaHref: (pathname: string) => string;
}

interface BuildManagedItemsState {
    labels: Record<string, string>;
    creditsAvailable: boolean;
    countPackageAvailable: boolean;
    paidPackageAvailable: boolean;
}

const MANAGED_MENU_KEYS = ['credits', 'package', 'version', 'manage-subscription'];
const MANAGED_MENU_PATHNAMES = ['credits', 'user-setting/amount', 'user-setting/package/ip', 'user-setting/package/management'];
const PERSONAL_HIDDEN_MENU_KEYS = ['settings', 'team'];

const LABELS: Record<UserCenterMenuLocale, Record<string, string>> = {
    cn: {
        history: '历史记录',
        allVersions: '所有版本',
        usage: '用量',
        credits: '积分',
        manageSubscription: '管理订阅',
    },
    en: {
        history: 'History',
        allVersions: 'All versions',
        usage: 'Usage',
        credits: 'Credits',
        manageSubscription: 'Manage Subscription',
    },
    jp: {
        history: '履歴',
        allVersions: 'すべてのバージョン',
        usage: '利用回数',
        credits: '積分',
        manageSubscription: 'サブスクリプション管理',
    },
    tw: {
        history: '歷史記錄',
        allVersions: '所有版本',
        usage: '用量',
        credits: '積分',
        manageSubscription: '管理訂閱',
    },
};

function getLabels(locale: UserCenterMenuLocale | string) {
    return LABELS[locale as UserCenterMenuLocale] || LABELS.en;
}

function hasCommercialPackage(packages: IPackageType[]) {
    return packages.some(item => item.package_type === 'COMMERCIAL');
}

function insertHistoryMenu(items: UserCenterMenuItem[], locale: UserCenterMenuLocale | string, createEurekaHref: (pathname: string) => string) {
    const labels = getLabels(locale);
    const result = [...items];
    result.splice(Math.min(1, result.length), 0, {
        href: createEurekaHref('history'),
        label: labels.history,
        icon: 'SolidHistoryRecord',
        key: 'history',
    });
    return result;
}

function normalizeMenuPath(href: string) {
    try {
        const url = new URL(href, 'https://eureka.local');
        const hashPath = url.hash.replace(/^#\/?/, '').split('?')[0];
        const path = hashPath || url.pathname;
        return path.replace(/^\/+|\/+$/g, '');
    } catch {
        return href.split('?')[0].replace(/^\/+|\/+$/g, '');
    }
}

function isManagedMenuItem(item: UserCenterMenuItem) {
    const key = item.key as string | undefined;
    if (key && MANAGED_MENU_KEYS.includes(key)) {
        return true;
    }

    if (!item.href) {
        return false;
    }

    return MANAGED_MENU_PATHNAMES.includes(normalizeMenuPath(item.href));
}

function getBaseItems(items: UserCenterMenuItem[]) {
    return items.filter(item => {
        if (isManagedMenuItem(item)) {
            return false;
        }
        return true;
    });
}

function filterPersonalHiddenItems(items: UserCenterMenuItem[]) {
    return items.filter(item => !PERSONAL_HIDDEN_MENU_KEYS.includes(item.key as string));
}

function createManagedMenuItem(options: CreateManagedMenuItemOptions) {
    return {
        href: options.createEurekaHref(options.pathname),
        label: options.label,
        icon: options.icon,
        key: options.key,
    };
}

function getBaseMenuItems(options: Pick<BuildUserCenterMenuItemsOptions, 'items' | 'locale' | 'isPersonalTenant' | 'createEurekaHref'>) {
    const baseItemsWithHistory = insertHistoryMenu(getBaseItems(options.items), options.locale, options.createEurekaHref);
    return options.isPersonalTenant ? filterPersonalHiddenItems(baseItemsWithHistory) : baseItemsWithHistory;
}

function buildCommonManagedItems(options: BuildUserCenterMenuItemsOptions, state: BuildManagedItemsState) {
    const items: UserCenterMenuItem[] = [];
    if (state.creditsAvailable) {
        items.push(createManagedMenuItem({
            key: 'credits',
            pathname: 'credits',
            label: state.labels.credits,
            icon: 'MemberPoint',
            createEurekaHref: options.createEurekaHref,
        }));
    }
    return items;
}

function buildEnterpriseManagedItems(options: BuildUserCenterMenuItemsOptions, state: BuildManagedItemsState) {
    const items: UserCenterMenuItem[] = buildCommonManagedItems(options, state);
    if (state.countPackageAvailable) {
        items.push(createManagedMenuItem({
            key: 'package',
            pathname: 'user-setting/amount',
            label: state.labels.usage,
            icon: 'Userpackage',
            createEurekaHref: options.createEurekaHref,
        }));
    }
    //中美的企业版展示所有版本
    items.push(createManagedMenuItem({
        key: 'version',
        pathname: 'user-setting/package/ip',
        label: state.labels.allVersions,
        icon: 'SolidRocket',
        createEurekaHref: options.createEurekaHref,
    }));
    return items;
}

function buildPersonalManagedItems(options: BuildUserCenterMenuItemsOptions, state: BuildManagedItemsState) {
    const items = buildCommonManagedItems(options, state);
    // 只有中国的个人版展示所有版本
    if (!getIsOversea()) {
        items.push(createManagedMenuItem({
            key: 'version',
            pathname: 'user-setting/package/ip',
            label: state.labels.allVersions,
            icon: 'SolidRocket',
            createEurekaHref: options.createEurekaHref,
        }));
    }
    if (state.paidPackageAvailable) {
        items.push(createManagedMenuItem({
            key: 'manage-subscription',
            pathname: 'user-setting/package/management',
            label: state.labels.manageSubscription,
            icon: 'SolidRocket',
            createEurekaHref: options.createEurekaHref,
        }));
    }
    return items;
}

function buildManagedItems(
    options: BuildUserCenterMenuItemsOptions,
    state: BuildManagedItemsState,
) {
    if (options.isEnterpriseTenant) {
        return buildEnterpriseManagedItems(options, state);
    }
    if (options.isPersonalTenant) {
        return buildPersonalManagedItems(options, state);
    }
    return [];
}

export function buildUserCenterMenuItems(options: BuildUserCenterMenuItemsOptions): UserCenterMenuItem[] {
    const labels = getLabels(options.locale);
    const paidPackageAvailable = options.packagesLoaded && hasCommercialPackage(options.packages);
    const creditsAvailable = options.control?.credit_package === true;
    const countPackageAvailable = options.control?.count_package === true;
    const baseItems = getBaseMenuItems(options);
    const managedItems = buildManagedItems(options, {
        labels,
        creditsAvailable,
        countPackageAvailable,
        paidPackageAvailable,
    });

    return [...managedItems, ...baseItems];
}
